/**
 * P5 — Low-stock hold countdown (Spec §5 / A4).
 * Displays fixed TTL remaining; activity does **not** extend the clock.
 * Cart retain + re-verify happens via {@link reVerifyAfterExpiry}.
 */

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface LowStockReservationCountdownProps {
  /** ISO-8601 expiry from InventoryReservation / checkout hold. */
  readonly expiresAt: string;
  /** Fired once when remaining hits 0 (parent should re-verify cart). */
  readonly onExpired?: () => void;
  /** Clock override for tests. */
  readonly nowMs?: number;
  readonly className?: string;
}

function formatRemaining(ms: number): string {
  const totalSec = Math.max(0, Math.ceil(ms / 1000));
  const minutes = Math.floor(totalSec / 60);
  const seconds = totalSec % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

export function resolveLowStockRemainingMs(
  expiresAt: string,
  nowMs: number = Date.now(),
): number {
  const end = Date.parse(expiresAt);
  if (!Number.isFinite(end)) {
    return 0;
  }
  return Math.max(0, end - nowMs);
}

export function LowStockReservationCountdown({
  expiresAt,
  onExpired,
  nowMs,
  className,
}: LowStockReservationCountdownProps): JSX.Element {
  const { t } = useTranslation('customer');
  const [liveNowMs, setLiveNowMs] = useState(() => Date.now());
  const clockMs = nowMs ?? liveNowMs;
  const remainingMs = resolveLowStockRemainingMs(expiresAt, clockMs);
  const expired = remainingMs <= 0;
  const onExpiredRef = useRef(onExpired);
  const lastFiredExpiryRef = useRef<string | null>(null);

  useEffect(() => {
    onExpiredRef.current = onExpired;
  }, [onExpired]);

  useEffect(() => {
    if (nowMs !== undefined) {
      return;
    }
    const id = window.setInterval(() => {
      setLiveNowMs(Date.now());
    }, 1000);
    return () => {
      window.clearInterval(id);
    };
  }, [nowMs]);

  useEffect(() => {
    if (!expired) {
      lastFiredExpiryRef.current = null;
      return;
    }
    if (lastFiredExpiryRef.current === expiresAt) {
      return;
    }
    lastFiredExpiryRef.current = expiresAt;
    onExpiredRef.current?.();
  }, [expired, expiresAt]);

  const label = expired
    ? t('shop.lowStockHold.expiredLabel')
    : t('shop.lowStockHold.countdownLabel', {
        time: formatRemaining(remainingMs),
      });

  return (
    <div
      role="status"
      aria-live="polite"
      aria-label={t('shop.lowStockHold.ariaLive')}
      data-testid="low-stock-reservation-countdown"
      data-expired={expired ? 'true' : 'false'}
      className={
        className ??
        'rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] px-4 py-3 text-sm text-[var(--color-on-surface)]'
      }
    >
      {label}
    </div>
  );
}
